import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Star,
  Loader2,
  Calendar,
  Clock,
  Video,
  Filter,
  Hash,
  X,
  ChevronLeft,
  ChevronRight,
  Radio,
  PlayCircle,
  Award,
  User,
  Image as ImageIcon,
  FolderOpen,
  Upload,
  Sparkles,
  Eye,
  ExternalLink,
} from 'lucide-react';
import { adminAPI, industriesAPI, speakersAPI } from '@/services/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatET, formatETInputDate, formatETInputTime, etInputToISO } from '@/utils/dateUtils';
import { RichTextEditor } from '@/components/RichTextEditor';
import MediaPickerModal from '@/components/common/MediaPickerModal';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { useDebounce } from '@/hooks/useDebounce';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function AdminWebinars() {
  const [webinars, setWebinars] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // AI Content Improvement
  const [aiProcessing, setAiProcessing] = useState(false);
  const [contentFile, setContentFile] = useState(null);
  
  // Search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'inactive'
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all'); // all, live, on_demand, certified
  const [industryFilter, setIndustryFilter] = useState('all');
  const [speakerFilter, setSpeakerFilter] = useState('all');
  // Date filter (same UX as public Webinars page)
  const [dateFilterMode, setDateFilterMode] = useState('none'); // none | specific | month | range
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const perPage = 20;
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingWebinar, setEditingWebinar] = useState(null);
  const [formData, setFormData] = useState(getEmptyFormData());
  
  // Helper states
  const [newObjective, setNewObjective] = useState('');
  const [newTag, setNewTag] = useState('');
  
  // Gallery dialog state
  const [showGalleryDialog, setShowGalleryDialog] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryCategory, setGalleryCategory] = useState('all');

  // Helper function to format file size
  function formatFileSize(bytes) {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  function getEmptyFormData() {
    return {
      title: '',
      slug: '',
      webinar_code: '',
      description: '',
      thumbnail_url: '',
      speaker_ids: [],
      industry_id: '',
      webinar_type: 'live',
      level: 'intermediate',
      status: 'draft',
      is_active: true,
      scheduled_date: '',
      scheduled_time: '',
      duration_minutes: 60,
      timezone: 'America/New_York',
      // Pricing
      price: 149,
      price_live: 149,
      price_recorded: 199,
      price_live_recorded: 249,
      price_digital_download: 349,
      price_live_usb: 399,
      price_group_live: 1399,
      // Content sections
      overview: '',
      areas_covered_text: '',
      why_attend: '',
      who_should_attend: '',
      topic_background: '',
      // AI Summary
      ai_summary_overview: '',
      ai_summary_takeaways: '',
      // Meta
      has_cpe_credits: false,
      cpe_credits: 0,
      learning_objectives: [],
      learning_objectives_text: '',
      tags: [],
      is_featured: false,
      // Materials (PDFs, docs, handouts)
      materials: [],
    };
  }

  // Materials upload state
  const [uploadingMaterial, setUploadingMaterial] = useState(false);

  useEffect(() => {
    fetchData();
  }, [currentPage, activeTab, statusFilter, typeFilter, industryFilter, speakerFilter, dateFilterMode, selectedDate, selectedMonth, selectedYear, dateRange, debouncedSearchTerm]);

  // Reset to page 1 when search changes
  useEffect(() => {
    if (currentPage !== 1) setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  useEffect(() => {
    fetchIndustriesAndSpeakers();
  }, []);

  const fetchIndustriesAndSpeakers = async () => {
    try {
      const [industriesRes, speakersRes] = await Promise.all([
        industriesAPI.getFlat(),
        // Include inactive speakers so admins can assign historical speakers
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/speakers-dropdown?include_inactive=true`).then(r => r.json()),
      ]);
      setIndustries(industriesRes.data || []);
      setSpeakers(speakersRes || []);
    } catch (error) {
      console.error('Error fetching industries/speakers:', error);
    }
  };

  const fetchGalleryImages = async (category = 'all') => {
    try {
      setGalleryLoading(true);
      const params = { per_page: 100 };
      if (category !== 'all') params.category = category;
      
      const response = await adminAPI.getGalleryImages(params);
      setGalleryImages(response.data.items || []);
    } catch (error) {
      console.error('Error fetching gallery images:', error);
    } finally {
      setGalleryLoading(false);
    }
  };

  const [showMediaPicker, setShowMediaPicker] = useState(false);

  const handleOpenGallery = () => {
    setShowMediaPicker(true);
  };

  const handleSelectGalleryImage = (imageUrl) => {
    setFormData({ ...formData, thumbnail_url: imageUrl });
    setShowMediaPicker(false);
    toast.success('Thumbnail selected from Media Library');
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: perPage,
      };
      
      // Handle Active vs Inactive tab logic using is_active_tab parameter
      if (activeTab === 'active') {
        // Active tab: only published and draft webinars (NOT disabled)
        params.is_active_tab = true;
        // Additional status filter within active tab
        if (statusFilter !== 'all') {
          params.status = statusFilter; // 'published' or 'draft'
        }
      } else {
        // Inactive tab: only disabled webinars
        params.is_active_tab = false;
        // Status filter is always 'disabled' for inactive tab, ignore statusFilter
      }
      
      // Type filter (live/on_demand/certified) - only for active tab
      if (activeTab === 'active' && typeFilter !== 'all') {
        if (typeFilter === 'certified') {
          params.has_cpe = true; // Filter by CPE certified
        } else {
          params.webinar_type = typeFilter; // 'live' or 'on_demand'
        }
      }
      
      if (industryFilter !== 'all') params.industry_id = industryFilter;
      if (speakerFilter !== 'all') params.speaker_id = speakerFilter;

      // Date filter -> server-side params (works across all pages)
      if (dateFilterMode === 'specific' && selectedDate) {
        const d = format(selectedDate, 'yyyy-MM-dd');
        params.date_from = d;
        params.date_to = d;
      } else if (dateFilterMode === 'month') {
        params.month = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}`;
      } else if (dateFilterMode === 'range' && dateRange?.from && dateRange?.to) {
        params.date_from = format(dateRange.from, 'yyyy-MM-dd');
        params.date_to = format(dateRange.to, 'yyyy-MM-dd');
      }

      if (debouncedSearchTerm && debouncedSearchTerm.trim()) {
        params.search = debouncedSearchTerm.trim();
      }
      
      const response = await adminAPI.getWebinars(params);
      const data = response.data;
      
      setWebinars(data.webinars || data || []);
      setTotalCount(data.total || (data.webinars || data || []).length);
      setTotalPages(Math.ceil((data.total || (data.webinars || data || []).length) / perPage));
    } catch (error) {
      console.error('Error fetching webinars:', error);
      toast.error('Failed to load webinars');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const generateWebinarCode = (type) => {
    const prefix = type === 'live' ? 'GRC-LIVE' : 'GRC-REC';
    const random = Math.random().toString(36).substring(2, 8).toUpperCase();
    return `${prefix}-${random}`;
  };

  const handleOpenForm = (webinar = null) => {
    if (webinar) {
      setEditingWebinar(webinar);
      const scheduledDate = webinar.scheduled_date ? new Date(webinar.scheduled_date) : null;
      setFormData({
        title: webinar.title || '',
        slug: webinar.slug || '',
        webinar_code: webinar.webinar_code || webinar.model || '',
        description: webinar.description || '',
        thumbnail_url: webinar.thumbnail_url || '',
        speaker_ids: webinar.speaker_ids || [],
        industry_id: webinar.industry_id || '',
        webinar_type: webinar.webinar_type || 'live',
        level: webinar.level || 'intermediate',
        status: webinar.status || 'draft',
        is_active: webinar.is_active !== false,
        scheduled_date: webinar.scheduled_date ? formatETInputDate(webinar.scheduled_date) : '',
        scheduled_time: webinar.scheduled_date ? formatETInputTime(webinar.scheduled_date) : '',
        duration_minutes: webinar.duration_minutes || 60,
        timezone: webinar.timezone || 'America/New_York',
        // Pricing
        price: webinar.price || 149,
        price_live: webinar.price_live || 149,
        price_recorded: webinar.price_recorded || 199,
        price_live_recorded: webinar.price_live_recorded || 249,
        price_digital_download: webinar.price_digital_download || 349,
        price_live_usb: webinar.price_live_usb || 399,
        price_group_live: webinar.price_group_live || 1399,
        // Content
        overview: webinar.overview || '',
        // Convert areas_covered array to HTML for rich text editor
        areas_covered_text: Array.isArray(webinar.areas_covered) && webinar.areas_covered.length > 0
          ? (webinar.areas_covered[0]?.includes('<') 
              ? webinar.areas_covered.join('') // Already HTML, join as-is
              : '<ul>' + webinar.areas_covered.map(item => `<li>${item}</li>`).join('') + '</ul>') // Convert to HTML list
          : (webinar.areas_covered || ''),
        why_attend: webinar.why_attend || '',
        who_should_attend: webinar.who_should_attend || '',
        topic_background: webinar.topic_background || '',
        // AI Summary
        ai_summary_overview: webinar.ai_summary?.overview || '',
        ai_summary_takeaways: Array.isArray(webinar.ai_summary?.takeaways) 
          ? webinar.ai_summary.takeaways.join('\n') 
          : (webinar.ai_summary?.takeaways || ''),
        // Meta
        has_cpe_credits: webinar.has_cpe_credits || false,
        cpe_credits: webinar.cpe_credits || 0,
        learning_objectives: webinar.learning_objectives || [],
        learning_objectives_text: Array.isArray(webinar.learning_objectives) && webinar.learning_objectives.length > 0
          ? (webinar.learning_objectives[0]?.includes('<') 
              ? webinar.learning_objectives.join('')
              : '<ul>' + webinar.learning_objectives.map(item => `<li>${item}</li>`).join('') + '</ul>')
          : (webinar.learning_objectives_text || ''),
        tags: webinar.tags || [],
        is_featured: webinar.is_featured || false,
      });
    } else {
      setEditingWebinar(null);
      setFormData(getEmptyFormData());
    }
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingWebinar(null);
    setFormData(getEmptyFormData());
  };

  const handleSaveWebinar = async () => {
    try {
      setSaving(true);
      
      let scheduled_date = null;
      if (formData.scheduled_date && formData.scheduled_time) {
        // Treat admin form input as ET wall-clock time → convert to UTC ISO
        scheduled_date = etInputToISO(formData.scheduled_date, formData.scheduled_time);
      }
      
      // Map status to is_active
      const is_active = formData.status === 'published';
      
      // Build ai_summary object from form fields
      const ai_summary = (formData.ai_summary_overview || formData.ai_summary_takeaways) ? {
        overview: formData.ai_summary_overview || '',
        takeaways: formData.ai_summary_takeaways 
          ? formData.ai_summary_takeaways.split('\n').filter(t => t.trim())
          : []
      } : null;
      
      const data = {
        ...formData,
        scheduled_date,
        is_active,
        ai_summary,
        // Generate code if empty
        webinar_code: formData.webinar_code || generateWebinarCode(formData.webinar_type),
        // Convert areas_covered_text (HTML) to areas_covered (single item array for backwards compatibility)
        areas_covered: formData.areas_covered_text ? [formData.areas_covered_text] : [],
        // Convert learning_objectives_text (HTML) to learning_objectives array
        learning_objectives: formData.learning_objectives_text ? [formData.learning_objectives_text] : [],
      };
      
      // Remove form-only fields
      delete data.scheduled_time;
      delete data.areas_covered_text;
      delete data.learning_objectives_text;
      delete data.ai_summary_overview;
      delete data.ai_summary_takeaways;
      
      if (editingWebinar) {
        await adminAPI.updateWebinar(editingWebinar.id, data);
        toast.success('Webinar updated successfully');
      } else {
        await adminAPI.createWebinar(data);
        toast.success('Webinar created successfully');
      }
      
      handleCloseForm();
      fetchData();
    } catch (error) {
      console.error('Error saving webinar:', error);
      toast.error('Failed to save webinar');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteWebinar = async (webinar) => {
    if (!confirm(`Delete "${webinar.title}"?`)) return;
    
    try {
      await adminAPI.deleteWebinar(webinar.id);
      toast.success('Webinar deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete webinar');
    }
  };

  // AI Content Improvement
  const handleAiImproveContent = async () => {
    if (!contentFile) {
      toast.error('Please select a PDF or Word document first');
      return;
    }

    setAiProcessing(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', contentFile);
      formDataUpload.append('content_type', 'webinar');

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/improvise-content`, {
        method: 'POST',
        body: formDataUpload,
      });

      const result = await response.json();

      if (result.success && result.improvised_content) {
        const content = result.improvised_content;
        
        // Update form with AI-generated content
        setFormData(prev => ({
          ...prev,
          description: content.overview || prev.description,
          areas_covered_text: Array.isArray(content.areas_covered) 
            ? '<ul>' + content.areas_covered.map(item => `<li>${item}</li>`).join('') + '</ul>'
            : prev.areas_covered_text,
          why_attend: content.why_should_attend || prev.why_attend,
          who_should_attend: content.who_should_attend || prev.who_should_attend,
          topic_background: content.topic_background || prev.topic_background,
          learning_objectives: content.learning_objectives 
            ? [content.learning_objectives] 
            : prev.learning_objectives,
        }));

        toast.success('Content improved with AI! Review and edit as needed.');
        setContentFile(null);
      } else {
        toast.error(result.detail || 'Failed to improve content');
      }
    } catch (error) {
      console.error('AI content error:', error);
      toast.error('Failed to process document');
    } finally {
      setAiProcessing(false);
    }
  };

  const addToList = (field, value, setter) => {
    if (!value.trim()) return;
    setFormData({ ...formData, [field]: [...(formData[field] || []), value.trim()] });
    setter('');
  };

  const removeFromList = (field, index) => {
    setFormData({
      ...formData,
      [field]: formData[field].filter((_, i) => i !== index)
    });
  };

  // Get speaker names for display
  const getSpeakerNames = (speakerIds) => {
    if (!speakerIds || speakerIds.length === 0) return 'No speaker';
    return speakerIds.map(id => {
      const speaker = speakers.find(s => s.id === id);
      return speaker ? `${speaker.first_name || ''} ${speaker.last_name || ''}`.trim() : '';
    }).filter(Boolean).join(', ') || 'Unknown';
  };

  // Get industry name for display
  const getIndustryName = (industryId) => {
    if (!industryId) return 'No Industry';
    const industry = industries.find(i => i.id === industryId);
    return industry ? (industry.display_name || industry.name) : 'Unknown';
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-700';
      case 'draft': return 'bg-yellow-100 text-yellow-700';
      case 'disabled': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getTypeBadgeColor = (type) => {
    return type === 'live' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700';
  };

  // Webinars come from the backend already filtered by `search` param
  const filteredWebinars = webinars;

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setStatusFilter('all');
    setTypeFilter('all');
  };

  return (
    <div className="flex h-[calc(100vh-8rem)]" data-testid="admin-webinars">
      {/* Left Panel - List */}
      <div className={`${showForm ? 'w-1/2 border-r border-slate-200' : 'w-full'} flex flex-col bg-white`}>
        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between p-4 border-b border-slate-200">
          <div>
            <h2 className="font-heading text-2xl font-bold text-navy-700">Webinar Management</h2>
            <p className="text-slate-500">Total: {totalCount} webinars</p>
          </div>
          <Button 
            onClick={() => handleOpenForm()}
            className="bg-navy-600 hover:bg-navy-700 rounded-xl"
            data-testid="create-webinar-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Webinar
          </Button>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200">
          <div className="flex">
            <button
              onClick={() => handleTabChange('active')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'active'
                  ? 'border-navy-600 text-navy-600 bg-navy-50'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
              data-testid="active-tab"
            >
              Active Webinars
            </button>
            <button
              onClick={() => handleTabChange('inactive')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'inactive'
                  ? 'border-navy-600 text-navy-600 bg-navy-50'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
              data-testid="inactive-tab"
            >
              Inactive Webinars
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-slate-200 bg-slate-50 space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search by title, code, speaker, topic..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded-lg"
            />
          </div>
          
          {/* Filter Row - single line, evenly spaced. Date filter first, before Status. */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Date Filter (same UX as public Webinars page) */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-44 h-10 text-sm justify-start font-normal rounded-lg"
                  data-testid="date-filter-trigger"
                >
                  <Calendar className="w-4 h-4 mr-2 shrink-0 text-slate-400" />
                  <span className="truncate">
                    {dateFilterMode === 'none' && 'All Dates'}
                    {dateFilterMode === 'specific' && selectedDate && format(selectedDate, 'MMM dd, yyyy')}
                    {dateFilterMode === 'specific' && !selectedDate && 'Select Date'}
                    {dateFilterMode === 'month' && `${MONTHS[selectedMonth]} ${selectedYear}`}
                    {dateFilterMode === 'range' && dateRange.from && dateRange.to &&
                      `${format(dateRange.from, 'MMM dd')} - ${format(dateRange.to, 'MMM dd')}`}
                    {dateFilterMode === 'range' && (!dateRange.from || !dateRange.to) && 'Select Range'}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-4" align="start" data-testid="date-filter-popover">
                {/* Mode Tabs */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <button
                    type="button"
                    data-testid="date-option-none"
                    onClick={() => { setDateFilterMode('none'); setSelectedDate(null); setDateRange({ from: null, to: null }); setCurrentPage(1); }}
                    className={`px-3 py-2 text-xs rounded-lg border transition-all ${
                      dateFilterMode === 'none' ? 'border-zinc-900 bg-zinc-50 text-zinc-900' : 'border-zinc-200 hover:border-zinc-300'
                    }`}
                  >
                    All Dates
                  </button>
                  <button
                    type="button"
                    data-testid="date-option-specific"
                    onClick={() => setDateFilterMode('specific')}
                    className={`px-3 py-2 text-xs rounded-lg border transition-all ${
                      dateFilterMode === 'specific' ? 'border-zinc-900 bg-zinc-50 text-zinc-900' : 'border-zinc-200 hover:border-zinc-300'
                    }`}
                  >
                    Specific Date
                  </button>
                  <button
                    type="button"
                    data-testid="date-option-month"
                    onClick={() => setDateFilterMode('month')}
                    className={`px-3 py-2 text-xs rounded-lg border transition-all ${
                      dateFilterMode === 'month' ? 'border-zinc-900 bg-zinc-50 text-zinc-900' : 'border-zinc-200 hover:border-zinc-300'
                    }`}
                  >
                    By Month
                  </button>
                  <button
                    type="button"
                    data-testid="date-option-range"
                    onClick={() => setDateFilterMode('range')}
                    className={`px-3 py-2 text-xs rounded-lg border transition-all ${
                      dateFilterMode === 'range' ? 'border-zinc-900 bg-zinc-50 text-zinc-900' : 'border-zinc-200 hover:border-zinc-300'
                    }`}
                  >
                    Date Range
                  </button>
                </div>

                {/* Specific Date Calendar */}
                {dateFilterMode === 'specific' && (
                  <CalendarComponent
                    mode="single"
                    selected={selectedDate}
                    onSelect={(d) => { setSelectedDate(d); setCurrentPage(1); }}
                    className="rounded-md border border-zinc-200"
                    data-testid="date-calendar-specific"
                  />
                )}

                {/* Month/Year Selector */}
                {dateFilterMode === 'month' && (
                  <div className="space-y-3">
                    <Select value={selectedMonth.toString()} onValueChange={(v) => { setSelectedMonth(parseInt(v)); setCurrentPage(1); }}>
                      <SelectTrigger className="w-full" data-testid="date-month-select">
                        <SelectValue placeholder="Select Month" />
                      </SelectTrigger>
                      <SelectContent>
                        {MONTHS.map((month, idx) => (
                          <SelectItem key={idx} value={idx.toString()}>{month}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={selectedYear.toString()} onValueChange={(v) => { setSelectedYear(parseInt(v)); setCurrentPage(1); }}>
                      <SelectTrigger className="w-full" data-testid="date-year-select">
                        <SelectValue placeholder="Select Year" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 8 }, (_, i) => new Date().getFullYear() - 2 + i).map((year) => (
                          <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Date Range Calendar */}
                {dateFilterMode === 'range' && (
                  <div>
                    <CalendarComponent
                      mode="range"
                      selected={dateRange}
                      onSelect={(r) => { setDateRange(r || { from: null, to: null }); setCurrentPage(1); }}
                      numberOfMonths={1}
                      className="rounded-md border border-zinc-200"
                      data-testid="date-calendar-range"
                    />
                    {dateRange.from && dateRange.to && (
                      <p className="text-xs text-zinc-500 mt-2 text-center">
                        {format(dateRange.from, 'MMM dd, yyyy')} - {format(dateRange.to, 'MMM dd, yyyy')}
                      </p>
                    )}
                  </div>
                )}

                {/* Clear Button */}
                {dateFilterMode !== 'none' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setDateFilterMode('none'); setSelectedDate(null); setDateRange({ from: null, to: null }); setCurrentPage(1); }}
                    className="w-full mt-3 text-zinc-600"
                    data-testid="date-clear-btn"
                  >
                    <X className="w-3 h-3 mr-1" />
                    Clear Date Filter
                  </Button>
                )}
              </PopoverContent>
            </Popover>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-40 h-10 rounded-lg" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {activeTab === 'active' ? (
                  <>
                    <SelectItem value="all">Status</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                  </>
                ) : (
                  <>
                    <SelectItem value="all">Status</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>

            {/* All Webinars Filter - Only for Active Tab */}
            {activeTab === 'active' && (
              <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-40 h-10 rounded-lg" data-testid="type-filter">
                  <Filter className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="All Webinars" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Webinars</SelectItem>
                  <SelectItem value="live">Live</SelectItem>
                  <SelectItem value="on_demand">Recorded</SelectItem>
                  <SelectItem value="certified">Certified</SelectItem>
                </SelectContent>
              </Select>
            )}

            {/* Industry Filter */}
            <SearchableSelect
              options={[
                { value: 'all', label: 'All Industries' },
                ...industries.map((ind) => ({ value: ind.id, label: ind.name }))
              ]}
              value={industryFilter}
              onValueChange={(v) => { setIndustryFilter(v || 'all'); setCurrentPage(1); }}
              placeholder="All Industries"
              searchPlaceholder="Search industries..."
              className="w-48 h-10 rounded-lg"
              icon={<Filter className="w-4 h-4 shrink-0 text-slate-400" />}
              dataTestId="industry-filter"
              preserveOrder={true}
            />

            {/* Speaker Filter */}
            <SearchableSelect
              options={[
                { value: 'all', label: 'All Speakers' },
                ...speakers.map((sp) => ({ value: sp.id, label: `${sp.first_name} ${sp.last_name}` }))
              ]}
              value={speakerFilter}
              onValueChange={(v) => { setSpeakerFilter(v || 'all'); setCurrentPage(1); }}
              placeholder="All Speakers"
              searchPlaceholder="Search speakers..."
              className="w-48 h-10 rounded-lg"
              icon={<Filter className="w-4 h-4 shrink-0 text-slate-400" />}
              dataTestId="speaker-filter"
            />
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-navy-500" />
            </div>
          ) : (
            <Table>
              <TableHeader className="sticky top-0 bg-white z-10">
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold w-[280px]">Webinar</TableHead>
                  <TableHead className="font-semibold">Code</TableHead>
                  <TableHead className="font-semibold">Speaker</TableHead>
                  <TableHead className="font-semibold">Live Date</TableHead>
                  <TableHead className="font-semibold">Type</TableHead>
                  <TableHead className="font-semibold">Price</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredWebinars.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                      No webinars found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredWebinars.map((webinar) => (
                    <TableRow 
                      key={webinar.id} 
                      className={`hover:bg-slate-50 cursor-pointer ${editingWebinar?.id === webinar.id ? 'bg-navy-50' : ''}`}
                      onClick={() => handleOpenForm(webinar)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-10 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0 relative">
                            {webinar.thumbnail_url ? (
                              <img src={webinar.thumbnail_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Video className="w-4 h-4 text-slate-400" />
                              </div>
                            )}
                            {webinar.has_cpe_credits && (
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-gold-500 rounded-full flex items-center justify-center">
                                <Award className="w-2.5 h-2.5 text-white" />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-navy-700 truncate max-w-[200px]" title={webinar.title}>
                              {webinar.title}
                            </p>
                            <p className="text-xs text-slate-500 truncate">
                              {getIndustryName(webinar.industry_id)}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {webinar.model && (
                            <Badge variant="outline" className="font-mono text-xs bg-amber-50 text-amber-700 border-amber-200">
                              {webinar.model}
                            </Badge>
                          )}
                          {webinar.webinar_code && webinar.webinar_code !== webinar.model && (
                            <Badge variant="outline" className="font-mono text-xs bg-blue-50 text-blue-700 border-blue-200">
                              {webinar.webinar_code}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <User className="w-3 h-3" />
                          <span className="truncate max-w-[100px]" title={getSpeakerNames(webinar.speaker_ids)}>
                            {getSpeakerNames(webinar.speaker_ids)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {(webinar.live_date || webinar.scheduled_date) ? (
                          <div className="flex items-center gap-1 text-sm">
                            <Calendar className="w-3 h-3 text-slate-400" />
                            <span className="text-slate-700">
                              {formatET(webinar.live_date || webinar.scheduled_date, 'MMM dd, yyyy')}
                            </span>
                            {(webinar.live_time || webinar.scheduled_time) && (
                              <span className="text-slate-500 text-xs">
                                {webinar.live_time || webinar.scheduled_time}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={getTypeBadgeColor(webinar.webinar_type)}>
                          {webinar.webinar_type === 'live' ? (
                            <><Radio className="w-3 h-3 mr-1" />Live</>
                          ) : (
                            <><PlayCircle className="w-3 h-3 mr-1" />Recorded</>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium text-navy-700">${webinar.price || webinar.price_live || 0}</span>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusBadgeColor(webinar.is_active ? 'published' : 'disabled')}>
                          {webinar.is_active ? 'Published' : 'Disabled'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              // Open webinar preview using slug (duplicates are auto-resolved)
                              window.open(`/webinars/${webinar.slug || webinar.id}`, '_blank');
                            }}
                            className="h-8 w-8 p-0"
                            title="Preview webinar on frontend"
                          >
                            <Eye className="w-4 h-4 text-blue-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleOpenForm(webinar); }}
                            className="h-8 w-8 p-0"
                            title="Edit webinar"
                          >
                            <Edit className="w-4 h-4 text-slate-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleDeleteWebinar(webinar); }}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                            title="Delete webinar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between p-4 border-t border-slate-200 bg-white">
          <p className="text-sm text-slate-500">
            Showing {Math.min((currentPage - 1) * perPage + 1, totalCount)} - {Math.min(currentPage * perPage, totalCount)} of {totalCount}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm px-2">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Right Panel - Form (Half Page Side Panel) */}
      {showForm && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/20 z-40"
            onClick={handleCloseForm}
          />
          <div className="fixed inset-y-0 right-0 w-full md:w-1/2 lg:w-2/5 z-50 bg-white shadow-2xl overflow-auto border-l border-slate-200">
            {/* Form Header - Sticky */}
            <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-slate-200 bg-white shadow-sm">
              <div>
                <h3 className="font-heading text-lg font-semibold text-navy-700">
                  {editingWebinar ? 'Edit Webinar' : 'Create New Webinar'}
                </h3>
                {editingWebinar?.model && (
                <div className="flex gap-2 mt-1">
                  <Badge className="bg-amber-100 text-amber-700 text-xs">
                    OpenCart: {editingWebinar.model}
                  </Badge>
                  {editingWebinar.webinar_code && (
                    <Badge className="bg-blue-100 text-blue-700 text-xs">
                      New: {editingWebinar.webinar_code}
                    </Badge>
                  )}
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCloseForm}
              className="h-8 w-8 p-0"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Form Content */}
          <div className="p-4">
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="mb-4 w-full grid grid-cols-5 text-xs">
                <TabsTrigger value="basic">Basic</TabsTrigger>
                <TabsTrigger value="content">Content</TabsTrigger>
                <TabsTrigger value="pricing">Pricing</TabsTrigger>
                <TabsTrigger value="schedule">Schedule</TabsTrigger>
                <TabsTrigger value="materials">Materials</TabsTrigger>
              </TabsList>

              {/* Basic Tab */}
              <TabsContent value="basic" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label>Title *</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        title: e.target.value,
                        slug: generateSlug(e.target.value)
                      })}
                      placeholder="Enter webinar title"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>URL Slug</Label>
                    <Input
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                      placeholder="webinar-url-slug"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Webinar Code</Label>
                    <Input
                      value={formData.webinar_code}
                      onChange={(e) => setFormData({ ...formData, webinar_code: e.target.value })}
                      placeholder="GRC-LIVE-XXXXXX"
                    />
                    <p className="text-xs text-slate-500">Auto-generated if empty</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Industry *</Label>
                    <SearchableSelect
                      options={industries.map((ind) => ({ value: ind.id, label: ind.name }))}
                      value={formData.industry_id}
                      onValueChange={(value) => setFormData({ ...formData, industry_id: value })}
                      placeholder="Select industry"
                      searchPlaceholder="Search industries..."
                      dataTestId="industry-select"
                      preserveOrder={true}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select 
                      value={formData.webinar_type} 
                      onValueChange={(value) => setFormData({ ...formData, webinar_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="live">Live Webinar</SelectItem>
                        <SelectItem value="on_demand">Recorded Webinar</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Level</Label>
                    <Select 
                      value={formData.level} 
                      onValueChange={(value) => setFormData({ ...formData, level: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="basic">Basic</SelectItem>
                        <SelectItem value="intermediate">Intermediate</SelectItem>
                        <SelectItem value="advanced">Advanced</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select 
                      value={formData.status} 
                      onValueChange={(value) => setFormData({ ...formData, status: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="published">Published</SelectItem>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="disabled">Disabled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="col-span-2 space-y-2">
                    <Label>Thumbnail</Label>
                    <div className="flex gap-2">
                      <Input
                        value={formData.thumbnail_url}
                        onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                        placeholder="https://... or browse gallery"
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleOpenGallery}
                        className="shrink-0"
                      >
                        <FolderOpen className="w-4 h-4 mr-2" />
                        Browse
                      </Button>
                    </div>
                    {formData.thumbnail_url && (
                      <div className="relative w-32 h-20 rounded-lg overflow-hidden border mt-2">
                        <img 
                          src={formData.thumbnail_url} 
                          alt="Preview" 
                          className="w-full h-full object-cover"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </div>
                    )}
                  </div>

                  <div className="col-span-2 space-y-2">
                    <Label>Speaker</Label>
                    <SearchableSelect
                      options={speakers.map((speaker) => ({
                        value: speaker.id,
                        label: `${speaker.full_name || `${speaker.first_name} ${speaker.last_name}`}${speaker.speaker_code ? ` (${speaker.speaker_code})` : ''}`
                      }))}
                      value={formData.speaker_ids[0] || ''}
                      onValueChange={(value) => {
                        setFormData({
                          ...formData,
                          speaker_ids: value ? [value] : []
                        });
                      }}
                      placeholder="Select a speaker"
                      searchPlaceholder="Search speakers..."
                      dataTestId="speaker-select"
                    />
                    <p className="text-xs text-slate-500">Speaker's code will be used for webinar code generation</p>
                  </div>

                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_featured}
                        onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                        className="w-4 h-4 rounded"
                      />
                      <span className="text-sm">Featured</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.has_cpe_credits}
                        onChange={(e) => setFormData({ ...formData, has_cpe_credits: e.target.checked })}
                        className="w-4 h-4 rounded"
                      />
                      <span className="text-sm">CPE Credits</span>
                    </label>
                  </div>

                  {formData.has_cpe_credits && (
                    <div className="space-y-2">
                      <Label>CPE Credits</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={formData.cpe_credits}
                        onChange={(e) => setFormData({ ...formData, cpe_credits: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Content Tab */}
              <TabsContent value="content" className="space-y-4">
                {/* AI Content Improvement Section - Disabled for production (emergentintegrations not available on Hostinger) */}
                {/* 
                <div className="bg-slate-50 border border-purple-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    <h4 className="font-medium text-purple-900">AI Content Improvement</h4>
                  </div>
                  <p className="text-sm text-purple-700">
                    Upload a PDF or Word document with webinar content. AI will generate professional descriptions for all content sections.
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) => setContentFile(e.target.files[0])}
                        className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-purple-100 file:text-purple-700 hover:file:bg-purple-200"
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={handleAiImproveContent}
                      disabled={!contentFile || aiProcessing}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      {aiProcessing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Improve Content
                        </>
                      )}
                    </Button>
                  </div>
                  {contentFile && (
                    <p className="text-xs text-purple-600">Selected: {contentFile.name}</p>
                  )}
                </div>
                */}

                <div className="space-y-2">
                  <Label>Webinar Overview</Label>
                  <RichTextEditor
                    key={`description-${editingWebinar?.id || 'new'}`}
                    id={`description-${editingWebinar?.id || 'new'}`}
                    value={formData.description}
                    onChange={(value) => setFormData({ ...formData, description: value })}
                    placeholder="Comprehensive webinar overview and description..."
                    minHeight="150px"
                  />
                  <p className="text-xs text-slate-500">This is the main description displayed on the webinar detail page.</p>
                </div>

                <div className="space-y-2">
                  <Label>Areas Covered</Label>
                  <RichTextEditor
                    key={`areas-${editingWebinar?.id || 'new'}`}
                    id={`areas-${editingWebinar?.id || 'new'}`}
                    value={formData.areas_covered_text}
                    onChange={(value) => setFormData({ ...formData, areas_covered_text: value })}
                    placeholder="List the key areas and topics covered..."
                    minHeight="100px"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Why Should You Attend?</Label>
                  <RichTextEditor
                    key={`why-attend-${editingWebinar?.id || 'new'}`}
                    id={`why-attend-${editingWebinar?.id || 'new'}`}
                    value={formData.why_attend}
                    onChange={(value) => setFormData({ ...formData, why_attend: value })}
                    placeholder="Explain why attendees should join..."
                    minHeight="100px"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Who Should Attend?</Label>
                  <RichTextEditor
                    key={`who-attend-${editingWebinar?.id || 'new'}`}
                    id={`who-attend-${editingWebinar?.id || 'new'}`}
                    value={formData.who_should_attend}
                    onChange={(value) => setFormData({ ...formData, who_should_attend: value })}
                    placeholder="Target audience description..."
                    minHeight="100px"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Topic Background</Label>
                  <RichTextEditor
                    key={`topic-${editingWebinar?.id || 'new'}`}
                    id={`topic-${editingWebinar?.id || 'new'}`}
                    value={formData.topic_background}
                    onChange={(value) => setFormData({ ...formData, topic_background: value })}
                    placeholder="Background information on the topic..."
                    minHeight="100px"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Learning Objectives</Label>
                  <RichTextEditor
                    key={`learning-objectives-${editingWebinar?.id || 'new'}`}
                    id={`learning-objectives-${editingWebinar?.id || 'new'}`}
                    value={formData.learning_objectives_text || ''}
                    onChange={(value) => setFormData({ ...formData, learning_objectives_text: value })}
                    placeholder="List the learning objectives for this webinar..."
                    minHeight="100px"
                  />
                  <p className="text-xs text-slate-500">What participants will learn from this webinar.</p>
                </div>

                {/* AI Summary Section */}
                <div className="bg-amber-50 border border-gold-200 rounded-lg p-4 space-y-4">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-gold-600" />
                    <h4 className="font-medium text-gold-900">AI Quick Summary</h4>
                    <span className="text-xs text-gold-600 bg-gold-100 px-2 py-0.5 rounded">Auto-generated</span>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-gold-800">Summary Overview</Label>
                    <Textarea
                      value={formData.ai_summary_overview}
                      onChange={(e) => setFormData({ ...formData, ai_summary_overview: e.target.value })}
                      placeholder="AI-generated overview will appear here..."
                      className="min-h-[100px] bg-white"
                    />
                    <p className="text-xs text-gold-600">2-3 sentence overview displayed on the webinar detail page.</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-gold-800">Key Takeaways</Label>
                    <Textarea
                      value={formData.ai_summary_takeaways}
                      onChange={(e) => setFormData({ ...formData, ai_summary_takeaways: e.target.value })}
                      placeholder="Enter key takeaways (one per line)..."
                      className="min-h-[120px] bg-white"
                    />
                    <p className="text-xs text-gold-600">Enter each takeaway on a new line. These appear as bullet points.</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Tags</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      placeholder="Add tag"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToList('tags', newTag, setNewTag))}
                    />
                    <Button type="button" variant="outline" onClick={() => addToList('tags', newTag, setNewTag)}>
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.tags.map((tag, idx) => (
                      <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                        {tag}
                        <X className="w-3 h-3 cursor-pointer" onClick={() => removeFromList('tags', idx)} />
                      </Badge>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Pricing Tab */}
              <TabsContent value="pricing" className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600 mb-2">
                    {formData.webinar_type === 'live' 
                      ? 'Live webinars have 6 pricing options. Options 1-5 are for 1 participant.'
                      : 'Recorded webinars have 2 pricing options (for 1 participant).'}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {formData.webinar_type === 'live' && (
                    <>
                      <div className="space-y-2">
                        <Label>1. Live Access ($) <span className="text-xs text-slate-500">- 1 Participant</span></Label>
                        <Input
                          type="number"
                          value={formData.price_live}
                          onChange={(e) => setFormData({ ...formData, price_live: parseFloat(e.target.value) || 0, price: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>2. Download Access ($) <span className="text-xs text-slate-500">- 1 Participant</span></Label>
                        <Input
                          type="number"
                          value={formData.price_recorded}
                          onChange={(e) => setFormData({ ...formData, price_recorded: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>3. Live + Download Access ($) <span className="text-xs text-slate-500">- 1 Participant</span></Label>
                        <Input
                          type="number"
                          value={formData.price_live_recorded}
                          onChange={(e) => setFormData({ ...formData, price_live_recorded: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>4. USB Flash Drive ($) <span className="text-xs text-slate-500">- 1 Participant</span></Label>
                        <Input
                          type="number"
                          value={formData.price_digital_download}
                          onChange={(e) => setFormData({ ...formData, price_digital_download: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>5. Live + USB Flash Drive ($) <span className="text-xs text-slate-500">- 1 Participant</span></Label>
                        <Input
                          type="number"
                          value={formData.price_live_usb}
                          onChange={(e) => setFormData({ ...formData, price_live_usb: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>6. Group Live (Up to 20) ($) <span className="text-xs text-slate-500">- Up to 20 Participants</span></Label>
                        <Input
                          type="number"
                          value={formData.price_group_live}
                          onChange={(e) => setFormData({ ...formData, price_group_live: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                    </>
                  )}

                  {formData.webinar_type === 'on_demand' && (
                    <>
                      <div className="space-y-2">
                        <Label>Recorded Access ($)</Label>
                        <Input
                          type="number"
                          value={formData.price_recorded}
                          onChange={(e) => setFormData({ ...formData, price_recorded: parseFloat(e.target.value) || 0, price: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>USB Drive ($)</Label>
                        <Input
                          type="number"
                          value={formData.price_digital_download}
                          onChange={(e) => setFormData({ ...formData, price_digital_download: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                    </>
                  )}
                </div>
              </TabsContent>

              {/* Schedule Tab */}
              <TabsContent value="schedule" className="space-y-4">
                {formData.webinar_type === 'live' ? (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Date</Label>
                        <Input
                          type="date"
                          value={formData.scheduled_date}
                          onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Time</Label>
                        <Input
                          type="time"
                          value={formData.scheduled_time}
                          onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Duration (minutes)</Label>
                        <Input
                          type="number"
                          value={formData.duration_minutes}
                          onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 60 })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Timezone</Label>
                        <Select 
                          value={formData.timezone} 
                          onValueChange={(value) => setFormData({ ...formData, timezone: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="America/New_York">Eastern (EST/EDT)</SelectItem>
                            <SelectItem value="America/Chicago">Central (CST/CDT)</SelectItem>
                            <SelectItem value="America/Denver">Mountain (MST/MDT)</SelectItem>
                            <SelectItem value="America/Los_Angeles">Pacific (PST/PDT)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="p-6 bg-slate-50 rounded-lg text-center">
                    <Video className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                    <p className="text-slate-600">Recorded webinars don't require scheduling.</p>
                    <p className="text-sm text-slate-500 mt-1">They are available on-demand.</p>
                    <div className="mt-4">
                      <Label>Duration (minutes)</Label>
                      <Input
                        type="number"
                        value={formData.duration_minutes}
                        onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 60 })}
                        className="w-32 mx-auto mt-2"
                      />
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* Materials Tab */}
              <TabsContent value="materials" className="space-y-4">
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <FolderOpen className="w-5 h-5 text-navy-600" />
                      <h3 className="font-semibold text-slate-700">Webinar Materials</h3>
                    </div>
                    {editingWebinar && (
                      <div className="relative">
                        <input
                          type="file"
                          id="material-upload"
                          className="hidden"
                          accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.zip"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            
                            // Check file size (max 50MB)
                            if (file.size > 50 * 1024 * 1024) {
                              toast.error('File size must be less than 50MB');
                              return;
                            }
                            
                            setUploadingMaterial(true);
                            try {
                              const formDataUpload = new FormData();
                              formDataUpload.append('file', file);
                              
                              const response = await adminAPI.uploadWebinarMaterial(editingWebinar.id, formDataUpload);
                              
                              // Update the form data with the new material
                              setFormData(prev => ({
                                ...prev,
                                materials: [...(prev.materials || []), response.data]
                              }));
                              
                              toast.success('Material uploaded successfully');
                            } catch (error) {
                              console.error('Upload error:', error);
                              toast.error('Failed to upload material');
                            } finally {
                              setUploadingMaterial(false);
                              e.target.value = '';
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById('material-upload')?.click()}
                          disabled={uploadingMaterial}
                          className="gap-2"
                        >
                          {uploadingMaterial ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4" />
                          )}
                          Upload File
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  <p className="text-sm text-slate-500 mb-4">
                    Upload PDFs, documents, or other materials for registered users to download.
                    {!editingWebinar && " (Save the webinar first to upload materials)"}
                  </p>

                  {/* Materials List */}
                  {formData.materials && formData.materials.length > 0 ? (
                    <div className="space-y-2">
                      {formData.materials.map((material, index) => (
                        <div 
                          key={material.id || index} 
                          className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-navy-100 rounded-lg flex items-center justify-center">
                              <FolderOpen className="w-5 h-5 text-navy-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-700 text-sm">{material.name}</p>
                              <p className="text-xs text-slate-500">
                                {material.file_type?.toUpperCase()} • {formatFileSize(material.file_size)}
                                {material.uploaded_at && ` • ${format(new Date(material.uploaded_at), 'MMM d, yyyy')}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <a 
                              href={material.file_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-navy-600 hover:text-navy-800"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                            {editingWebinar && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={async () => {
                                  try {
                                    await adminAPI.deleteWebinarMaterial(editingWebinar.id, material.id);
                                    setFormData(prev => ({
                                      ...prev,
                                      materials: prev.materials.filter(m => m.id !== material.id)
                                    }));
                                    toast.success('Material deleted');
                                  } catch (error) {
                                    toast.error('Failed to delete material');
                                  }
                                }}
                                className="text-red-600 hover:text-red-800 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-white rounded-lg border border-dashed border-slate-300">
                      <FolderOpen className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                      <p className="text-slate-500 text-sm">No materials uploaded yet</p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-between p-4 border-t border-slate-200 bg-white">
            <Button variant="outline" onClick={handleCloseForm}>
              Cancel
            </Button>
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => { setFormData({ ...formData, status: 'draft' }); handleSaveWebinar(); }}
                disabled={saving}
              >
                Save as Draft
              </Button>
              <Button 
                onClick={handleSaveWebinar}
                disabled={saving}
                className="bg-navy-600 hover:bg-navy-700"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingWebinar ? 'Save Changes' : 'Create Webinar'}
              </Button>
            </div>
          </div>
        </div>
        </>
      )}

      {/* Gallery Selection Dialog */}
      <MediaPickerModal
        open={showMediaPicker}
        onClose={() => setShowMediaPicker(false)}
        onSelect={(url) => handleSelectGalleryImage(url)}
        folder="webinars"
      />
    </div>
  );
}
